/**
 * UTILITAIRES POUR LA GESTION DES COMPTES
 * Centralise toutes les fonctions réutilisables pour les comptes
 */

import { VALIDATION_STATUS } from "../../service/utils/validationStatus";

/**
 * Formate un numéro de téléphone international
 * @param {string} phoneNumber - Numéro de téléphone à formater
 * @returns {string} Numéro formaté
 */
export const formatPhoneNumber = (phoneNumber) => {
  if (!phoneNumber) return "";
  let match = phoneNumber.match(/^(\+?\d{3})(\d{8,})$/);
  if (!match) return phoneNumber;
  
  let code = match[1];
  let local = match[2];
  if (!code.startsWith("+")) code = "+" + code;
  
  if (code === "+237" && local.length === 9) {
    return `(${code}) ${local.replace(/(\d{3})(\d{2})(\d{2})(\d{2})/, "$1 $2 $3 $4")}`;
  }
  
  return `(${code}) ${local.replace(/(\d{2,3})(?=(\d{2})+(?!\d))/g, "$1 ")}`.trim();
};

/**
 * Formate un montant en devise XAF
 * @param {number} amount - Montant à formater
 * @returns {string} Montant formaté
 */
export const formatCurrency = (amount) => {
  return new Intl.NumberFormat('fr-FR', {
    style: 'currency',
    currency: 'XAF'
  }).format(amount || 0);
};

/**
 * Filtre les comptes selon les critères de recherche
 * @param {Array} accounts - Liste des comptes
 * @param {string} searchTerm - Terme de recherche
 * @param {string} accountTypeFilter - Filtre par type de compte
 * @param {string} statusFilter - Filtre par statut de validation
 * @returns {Array} Comptes filtrés
 */
export const filterAccounts = (accounts, searchTerm, accountTypeFilter, statusFilter) => {
  return accounts.filter(account => {
    // Filtre par recherche
    const matchesSearch = !searchTerm || 
      account.companyName?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      account.firstName?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      account.lastName?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      account.plainCode?.toString().includes(searchTerm);

    // Filtre par type de compte
    const matchesType = !accountTypeFilter || account.accountTypeName === accountTypeFilter;

    // Filtre par statut de validation
    const matchesStatus = !statusFilter || account.validationStatus === parseInt(statusFilter);

    return matchesSearch && matchesType && matchesStatus;
  });
};

/**
 * Trie les comptes selon les critères
 * @param {Array} accounts - Liste des comptes
 * @param {string} cashFilter - Tri par cash
 * @param {string} aucFilter - Tri par AUC
 * @returns {Array} Comptes triés
 */
export const sortAccounts = (accounts, cashFilter, aucFilter) => {
  const sortedAccounts = [...accounts];

  if (cashFilter === 'asc') {
    sortedAccounts.sort((a, b) => (a.cash || 0) - (b.cash || 0));
  } else if (cashFilter === 'desc') {
    sortedAccounts.sort((a, b) => (b.cash || 0) - (a.cash || 0));
  }

  if (aucFilter === 'asc') {
    sortedAccounts.sort((a, b) => (a.cashAUC || 0) - (b.cashAUC || 0));
  } else if (aucFilter === 'desc') {
    sortedAccounts.sort((a, b) => (b.cashAUC || 0) - (a.cashAUC || 0));
  }

  return sortedAccounts;
};

/**
 * Vérifie si un compte peut être soumis à validation
 * @param {Object} account - Compte à vérifier
 * @returns {boolean} True si soumissible
 */
export const canSubmitToValidation = (account) => {
  return (account.validationStatus === VALIDATION_STATUS.DRAFT || account.validationStatus === VALIDATION_STATUS.REJECTED );
};

/**
 * Obtient le nom d'affichage d'un compte
 * @param {Object} account - Compte
 * @returns {string} Nom d'affichage
 */
export const getAccountDisplayName = (account) => {
  return account.companyName || `${account.firstName} ${account.lastName}`;
};